import React, { useEffect, useState, useContext } from 'react';
import { Modal, Table, Switch, Input, Select, Row, Col, message, Checkbox, Button } from 'antd';
import { GetAulaAlumnesModal } from '../../dao/GetAulaAlumnesModal';
import { ToggleUsuariAula } from '../../dao/ToggleUsuariAula';
import { CursosList } from '../../constants/CursosList';
import { AuthContext } from '../../config/AuthContext';
import { GetAlumneId } from '../../dao/GetAlumneId';
import { DefaultAulumneInfo } from '../../model/AlumneInfo';

interface Props {
    visible: boolean;
    onClose: () => void;
    aulaId: string;
    aulaName: string;
}

const AulaStudentsModal: React.FC<Props> = ({ visible, onClose, aulaId, aulaName }) => {
    const [students, setStudents] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterCurs, setFilterCurs] = useState<string | null>(null);
    const [filterGrup, setFilterGrup] = useState<string | null>(null);
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Auth context
    const { userId, setLoggedIn, setUserId, setUser, setAlumneInfo, setUserPermisos } = useContext(AuthContext);

    useEffect(() => {
        if (visible && aulaId) {
            loadStudents();
        }
    }, [visible, aulaId]);

    const loadStudents = async () => {
        setLoading(true);
        const data = await GetAulaAlumnesModal(aulaId);
        setStudents(data);
        setLoading(false);
    };

    const handleToggle = async (userId: string, checked: boolean) => {
        try {
            await ToggleUsuariAula(aulaId, userId, checked ? 1 : 0);
            setStudents(prev => prev.map(s => s.id === userId ? { ...s, in_aula: checked ? 1 : 0 } : s));
            message.success('Actualizado correctamente');
        } catch (e) {
            message.error('Error al actualizar');
        }
    };

    const handleImpersonate = async (studentId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ adminId: userId, targetId: studentId })
            });

            if (response.ok) {
                const data = await response.json();
                const { token, permisos } = data;

                // Get Alumne Info
                let alu = DefaultAulumneInfo;
                if (permisos && permisos.length > 0) {
                    const res = await GetAlumneId(permisos[0].id, token);
                    if (res) alu = res;
                }

                // Save admin state if not already saved
                if (!localStorage.getItem('adminToken')) {
                    localStorage.setItem('adminToken', localStorage.getItem('authToken') || '');
                    localStorage.setItem('admin_userId', localStorage.getItem('userId') || '');
                    localStorage.setItem('admin_user', localStorage.getItem('user') || '');
                    localStorage.setItem('admin_userPermisos', localStorage.getItem('userPermisos') || '');
                    localStorage.setItem('admin_alumneInfo', localStorage.getItem('alumneInfo') || '');
                }

                localStorage.setItem('authToken', token);

                // Update Context
                setLoggedIn(true);
                setUserId(String(permisos[0].id));
                setUser(permisos[0]);
                setUserPermisos(permisos);
                setAlumneInfo(alu); // This updates avatar, name etc

                message.success('Impersonando usuario...');

                // Reload to fully apply state changes in all components
                window.location.reload();

            } else {
                message.error('Error al impersonar: ' + response.statusText);
            }
        } catch (e) {
            console.error(e);
            message.error('Error de conexión');
        }
    };

    const filteredStudents = students.filter(s => {
        const matchesSearch = searchText ? (
            (s.nom?.toLowerCase() || "").includes(searchText.toLowerCase()) ||
            (s.cognoms?.toLowerCase() || "").includes(searchText.toLowerCase())
        ) : true;
        // Check if filterCurs is defined and matches
        const matchesCurs = filterCurs ? s.curs === filterCurs : true;

        // Check if filterGrup is defined and matches
        const matchesGrup = filterGrup ? s.grup === filterGrup : true;

        // Check "show selected only"
        const matchesSelected = showSelectedOnly ? s.in_aula === 1 : true;

        return matchesSearch && matchesCurs && matchesGrup && matchesSelected;
    });

    const columns = [
        { title: 'Nom', dataIndex: 'nom', key: 'nom' },
        { title: 'Cognoms', dataIndex: 'cognoms', key: 'cognoms' },
        { title: 'Curs', dataIndex: 'curs', key: 'curs' },
        { title: 'Grup', dataIndex: 'grup', key: 'grup' },
        {
            title: 'En Aula',
            key: 'in_aula',
            render: (_: any, record: any) => (
                <Switch checked={record.in_aula === 1} onChange={(checked) => handleToggle(record.id, checked)} />
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_: any, record: any) => (
                record.in_aula === 1 && (
                    <Button type="link" onClick={() => handleImpersonate(record.id)}>
                        Impersonar
                    </Button>
                )
            )
        }
    ];

    // Cursos options (child items from CursosList, e.g. 1SMX)
    const cursOptions = CursosList.flatMap(c => c.children || []).map(ch => ({ label: ch.label, value: ch.value }));

    // Grup options (parent items, e.g. grau-mitja)
    const grupOptions = CursosList.map(c => ({ label: c.label, value: c.value }));

    return (
        <Modal title={`Gestionar Alumnos - ${aulaName}`} open={visible} onCancel={onClose} width={900} footer={null}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Input placeholder="Buscar por nombre..." value={searchText} onChange={e => setSearchText(e.target.value)} />
                </Col>
                <Col span={6}>
                    <Select
                        placeholder="Filtrar por cicle"
                        allowClear
                        style={{ width: '100%' }}
                        onChange={setFilterCurs}
                        options={cursOptions}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        placeholder="Filtrar por grupo"
                        allowClear
                        style={{ width: '100%' }}
                        onChange={setFilterGrup}
                        options={grupOptions}
                    />
                </Col>
                <Col span={6} style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={showSelectedOnly} onChange={e => setShowSelectedOnly(e.target.checked)}>
                        Solo seleccionados
                    </Checkbox>
                </Col>
            </Row>
            <Table dataSource={filteredStudents} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} />
        </Modal>
    );
};
export default AulaStudentsModal;
